// FILE: client/src/screens/admin/AdminCategoryFormScreen.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";

import Uploader from "../../components/images/Uploader";

import {
  adminCreateCategory,
  adminGetCategory,
  adminUpdateCategory,
  resetAdminCategoryCreate,
  resetAdminCategoryDetails,
  resetAdminCategoryUpdate,
} from "../../actions/categoryActions";
import {
  CATALOG_KEYS,
  CATALOGS,
  JEWELRY_HIRE_CATALOG_KEY,
  isValidCatalogKey,
} from "../../constants/shopMeta";
import { getApiErrorMessage } from "../../utils/errorMessages";

const DEFAULT_SHOP_KEY = CATALOG_KEYS?.[0] || "haberdashery";

const normalizeImages = (images = []) => {
  const raw = Array.isArray(images) ? images : images ? [images] : [];

  return raw
    .map((image) => {
      if (!image) return null;

      if (typeof image === "string") {
        const url = image.trim();

        if (!url) return null;

        return {
          url,
          public_id: "",
          alt: "",
          caption: "",
        };
      }

      const url = String(image.url || image.secure_url || "").trim();

      if (!url) return null;

      return {
        url,
        public_id: String(image.public_id || "").trim(),
        alt: String(image.alt || "").trim(),
        caption: String(image.caption || "").trim(),
      };
    })
    .filter(Boolean);
};

const getErrorMessage = (error) =>
  getApiErrorMessage(error, {
    fallback:
      "We could not save this category right now. Please try again later.",
  });

const getInitialShopKey = (search, routeShopKey = "") => {
  const searchParams = new URLSearchParams(search);
  const queryShopKey = searchParams.get("shopKey");

  if (isValidCatalogKey(queryShopKey)) return queryShopKey.trim();
  if (isValidCatalogKey(routeShopKey)) return routeShopKey.trim();
  return DEFAULT_SHOP_KEY;
};

const AdminCategoryFormScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const categoryId = params.id || params.categoryId;
  const isEditMode = Boolean(categoryId);

  const initialShopKey = useMemo(
    () => getInitialShopKey(location.search, params.shopKey),
    [location.search, params.shopKey],
  );

  const [shopKey, setShopKey] = useState(initialShopKey);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isPublished, setIsPublished] = useState(true);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const isJewelryCategory = shopKey === JEWELRY_HIRE_CATALOG_KEY;

  useEffect(() => {
    dispatch(resetAdminCategoryCreate());
    dispatch(resetAdminCategoryUpdate());

    return () => {
      dispatch(resetAdminCategoryDetails());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!isEditMode) return;

    let isMounted = true;

    const fetchCategory = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await dispatch(adminGetCategory(categoryId));

        if (!isMounted) return;

        setShopKey(
          isValidCatalogKey(data?.shopKey)
            ? data.shopKey.trim()
            : initialShopKey,
        );
        setName(data?.name || "");
        setSlug(data?.slug || "");
        setDescription(data?.description || "");
        setImages(normalizeImages(data?.images));
        setDisplayOrder(data?.displayOrder ?? 0);
        setIsPublished(data?.isPublished !== false);
      } catch (err) {
        if (!isMounted) return;

        const message = getErrorMessage(err);
        setError(message);
        toast.error(message);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchCategory();

    return () => {
      isMounted = false;
    };
  }, [categoryId, dispatch, initialShopKey, isEditMode]);

  const submitHandler = async (event) => {
    event.preventDefault();

    const safeShopKey = String(shopKey || "").trim();
    const safeName = String(name || "").trim();
    const safeDisplayOrder = Number(displayOrder);

    if (!isValidCatalogKey(safeShopKey)) {
      toast.error("Please select a valid catalogue.");
      return;
    }

    if (!safeName) {
      toast.error("Category name is required.");
      return;
    }

    if (
      isJewelryCategory &&
      (!Number.isInteger(safeDisplayOrder) ||
        safeDisplayOrder < 0 ||
        safeDisplayOrder > 9999)
    ) {
      toast.error("Display order must be a whole number between 0 and 9999.");
      return;
    }

    const payload = {
      shopKey: safeShopKey,
      name: safeName,
      slug: String(slug || "").trim(),
      description: String(description || "").trim(),
      images: normalizeImages(images),
      ...(isJewelryCategory ? { displayOrder: safeDisplayOrder } : {}),
      isPublished,
    };

    setSaving(true);
    setError("");

    try {
      if (isEditMode) {
        await dispatch(adminUpdateCategory(categoryId, payload));
        toast.success("Category updated.");
      } else {
        await dispatch(adminCreateCategory(payload));
        toast.success("Category created.");
      }

      navigate(-1);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container fluid>
      <Row className="justify-content-center">
        <Col lg={9} xl={8}>
          <Card className="shadow-sm">
            <Card.Header>
              <h1 className="h4 mb-0">
                {isEditMode ? "Edit Category" : "Create Category"}
              </h1>
            </Card.Header>

            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <Form onSubmit={submitHandler}>
                  {error ? <Alert variant="danger">{error}</Alert> : null}

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="shopKey">
                        <Form.Label>Catalogue</Form.Label>

                        <Form.Select
                          value={shopKey}
                          onChange={(event) => setShopKey(event.target.value)}
                          required
                          disabled={isEditMode || saving}
                        >
                          {CATALOGS.map((shop) => (
                            <option key={shop.shopKey} value={shop.shopKey}>
                              {shop.name}
                            </option>
                          ))}
                        </Form.Select>

                        {isEditMode ? (
                          <Form.Text className="text-muted">
                            Catalogue cannot be changed after category creation.
                          </Form.Text>
                        ) : null}
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3" controlId="isPublished">
                        <Form.Label>Status</Form.Label>

                        <Form.Select
                          value={isPublished ? "true" : "false"}
                          onChange={(event) =>
                            setIsPublished(event.target.value === "true")
                          }
                          disabled={saving}
                        >
                          <option value="true">Published</option>
                          <option value="false">Draft</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3" controlId="name">
                    <Form.Label>Category Name</Form.Label>

                    <Form.Control
                      type="text"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      placeholder="Enter category name"
                      required
                      disabled={saving}
                    />
                  </Form.Group>

                  {isJewelryCategory ? (
                    <Form.Group className="mb-3" controlId="displayOrder">
                      <Form.Label>Display Order</Form.Label>

                      <Form.Control
                        type="number"
                        min="0"
                        max="9999"
                        step="1"
                        value={displayOrder}
                        onChange={(event) =>
                          setDisplayOrder(event.target.value)
                        }
                        disabled={saving}
                        required
                      />

                      <Form.Text className="text-muted">
                        Lower numbers appear first. The first published category
                        becomes the large featured image.
                      </Form.Text>
                    </Form.Group>
                  ) : null}

                  {!isJewelryCategory ? (
                    <>
                      <Form.Group className="mb-3" controlId="slug">
                        <Form.Label>Slug</Form.Label>

                        <Form.Control
                          type="text"
                          value={slug}
                          onChange={(event) => setSlug(event.target.value)}
                          placeholder="Leave blank to auto-generate"
                          disabled={saving}
                        />

                        <Form.Text className="text-muted">
                          Leave blank to generate from the category name.
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-3" controlId="description">
                        <Form.Label>Description</Form.Label>

                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={description}
                          onChange={(event) =>
                            setDescription(event.target.value)
                          }
                          placeholder="Enter category description"
                          disabled={saving}
                        />
                      </Form.Group>
                    </>
                  ) : null}

                  <Card className="mb-4">
                    <Card.Header>
                      <strong>
                        {isJewelryCategory
                          ? "Main Category Image"
                          : "Category Images"}
                      </strong>
                    </Card.Header>

                    <Card.Body>
                      <Uploader
                        existingImages={images}
                        setImageUrls={setImages}
                        folder={`categories/${shopKey}`}
                        maxImages={isJewelryCategory ? 1 : undefined}
                      />

                      <Form.Text className="text-muted d-block mt-2">
                        {isJewelryCategory
                          ? "Upload one image to represent this category on the Jewellery page."
                          : "Upload images first, then save the category. The first image is treated as the cover image."}
                      </Form.Text>
                    </Card.Body>
                  </Card>

                  <div className="d-flex gap-2">
                    <Button type="submit" variant="primary" disabled={saving}>
                      {saving ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Saving...
                        </>
                      ) : isEditMode ? (
                        "Update Category"
                      ) : (
                        "Create Category"
                      )}
                    </Button>

                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={() => navigate(-1)}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminCategoryFormScreen;
